import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import matchService from '../services/matchService';
import teamService from '../services/teamService';

const MatchDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();

	const [match, setMatch] = useState(null);
	const [teams, setTeams] = useState([]);
	const [homeTeam, setHomeTeam] = useState('');
	const [awayTeam, setAwayTeam] = useState('');
	const [matchType, setMatchType] = useState('FOOTBALL');
	const [date, setDate] = useState('');
	const [homeTeamPoints, setHomeTeamPoints] = useState(0);
	const [awayTeamPoints, setAwayTeamPoints] = useState(0);
	const [scoreHome, setScoreHome] = useState(0);
	const [scoreAway, setScoreAway] = useState(0);
	const [drawPoints, setDrawPoints] = useState(0);
	const [sportDbApiId, setSportDbApiId] = useState(0);
	const [matchStatus, setMatchStatus] = useState('NOT_STARTED');
	const [error, setError] = useState('');

	useEffect(() => {
		const fetchData = async () => {
			try {
				const matchData = await matchService.getMatchById(id);
				setMatch(matchData);
				setHomeTeam(matchData.homeTeam.id);
				setAwayTeam(matchData.awayTeam.id);
				setDate(new Date(matchData.date).toISOString().slice(0, 16));
				setMatchType(matchData.sportType);
				setHomeTeamPoints(matchData.homeTeamPoints);
				setAwayTeamPoints(matchData.awayTeamPoints);
				setScoreHome(matchData.scoreHome);
				setScoreAway(matchData.scoreAway);
				setDrawPoints(matchData.drawPoints);
				setSportDbApiId(matchData.sportDbApiMatchId);
				setMatchStatus(matchData.matchStatus);
			} catch (err) {
				setError('Failed to fetch match data');
				console.error(err);
			}
		};

		const fetchTeams = async () => {
			try {
				const teamsData = await teamService.getAllTeams();
				setTeams(teamsData);
			} catch (err) {
				setError('Failed to fetch teams');
				console.error(err);
			}
		};

		fetchData();
		fetchTeams();
	}, [id]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (homeTeamPoints < 0 || awayTeamPoints < 0 || drawPoints < 0) {
			alert('Points must be non-negative!');
			return;
		}

		if (scoreHome < 0 || scoreAway < 0) {
			alert('Scores must be non-negative!');
			return;
		}
		
		const updatedMatch = {
			homeTeam: { id: homeTeam },
			awayTeam: { id: awayTeam },
			date: date,
			sportType: matchType,
			homeTeamPoints,
			awayTeamPoints,
			scoreHome,
			scoreAway,
			drawPoints,
			sportDbApiId,
			matchStatus
		};

		try {
			await matchService.updateAndProcessMatch(id, updatedMatch);
			alert('Match updated and results processed!');
			navigate('/matches');
		} catch (err) {
			setError('Failed to update match or process results');
			console.error(err);
		}
	};

	const handleCancel = () => {
		navigate('/matches');
	};

	if (error) {
		return <div style={{ color: 'red' }}>{error}</div>;
	}

	if (!match) return <div>Loading match data...</div>;

	return (
		<div>
			<h2>Edit Match</h2>
			<form onSubmit={handleSubmit}>
				<div>
					<label>Match Type:</label>
					<select
						value={matchType}
						onChange={(e) => setMatchType(e.target.value)}
						required
					>
						<option value="FOOTBALL">Football</option>
						<option value="HOCKEY">Hockey</option>
					</select>
				</div>

				<div>
					<label>Home Team:</label>
					<select value={homeTeam} onChange={(e) => setHomeTeam(e.target.value)} required>
						{teams.map((team) => (
							<option key={team.id} value={team.id}>
								{team.name}
							</option>
						))}
					</select>
				</div>

				<div>
					<label>Away Team:</label>
					<select value={awayTeam} onChange={(e) => setAwayTeam(e.target.value)} required>
						{teams.map((team) => (
							<option key={team.id} value={team.id}>
								{team.name}
							</option>
						))}
					</select>
				</div>

				<div>
					<label>Date:</label>
					<input
						type="datetime-local"
						value={date}
						onChange={(e) => setDate(e.target.value)}
						required
					/>
				</div>

				<div>
					<label>Points for Home Win:</label>
					<input
						type="number"
						value={homeTeamPoints}
						onChange={(e) => setHomeTeamPoints(parseInt(e.target.value, 10))}
						required
					/>
				</div>

				<div>
					<label>Points for Away Win:</label>
					<input
						type="number"
						value={awayTeamPoints}
						onChange={(e) => setAwayTeamPoints(parseInt(e.target.value, 10))}
						required
					/>
				</div>

				<div>
					<label>Points for Draw:</label>
					<input
						type="number"
						value={drawPoints}
						onChange={(e) => setDrawPoints(parseInt(e.target.value, 10))}
						required
					/>
				</div>

				<div>
					<label>Home Score:</label>
					<input
						type="number"
						value={scoreHome}
						onChange={(e) => setScoreHome(parseInt(e.target.value, 10))}
						required
					/>
				</div>

				<div>
					<label>Away Score:</label>
					<input
						type="number"
						value={scoreAway}
						onChange={(e) => setScoreAway(parseInt(e.target.value, 10))}
						required
					/>
				</div>
				
				<div>
					<label>SportDB API ID:</label>
					<input
						type="number"
						value={sportDbApiId}
						onChange={(e) => setSportDbApiId(parseInt(e.target.value, 10))}
						required
					/>
				</div>
				
				<div>
					<label htmlFor="match-status-select">Match Status:</label>
					<select
						id="match-status-select"
						value={matchStatus}
						onChange={(e) => setMatchStatus(e.target.value)}
						required
					>
						<option value="TBD">TBD</option>
						<option value="NS">Not Started</option>
						<option value="FT">Finished</option>
						<option value="1H">1st Half</option>
						<option value="HT">Half Time</option>
						<option value="2H">2nd Half</option>
						<option value="ET">Extra Time</option>
						<option value="P">Penalty</option>
						<option value="AET">After Extra Time</option>
						<option value="PEN">Penalties</option>
						<option value="BT">Break Time</option>
						<option value="SUSP">Suspended</option>
						<option value="INT">Interrupted</option>
						<option value="PST">Postponed</option>
						<option value="CANC">Canceled</option>
						<option value="ABD">Abandoned</option>
						<option value="AWD">Awarded</option>
						<option value="WO">Walkover</option>
						<option value="P1">Period 1</option>
						<option value="P2">Period 2</option>
						<option value="P3">Period 3</option>
						<option value="OT">Overtime</option>
						<option value="PT">Penalty Time</option>
						<option value="AW">Away Win</option>
						<option value="POST">Posted</option>
						<option value="INTR">Interrupted</option>
						<option value="AOT">Additional Overtime</option>
						<option value="AP">Additional Penalties</option>
					</select>
				</div>

				<button type="submit">Save Changes</button>
				<button type="button" onClick={handleCancel}>
					Cancel
				</button>
				<button
					type="button"
					onClick={async () => {
						try {
							await matchService.adminProcessMatchResults(id);
							alert('Results processed successfully!');
						} catch (err) {
							setError('Failed to process match results as admin');
							console.error(err);
						}
					}}
				>
				 Process Results
				</button>
			</form>
		</div>
	);
};

export default MatchDetails;
